import { useRef } from 'react';
import type { ScoutingSelectionStage } from '@/marketing/models/campaignScouting';
import { Button } from '@/marketing/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/marketing/components/ui/dialog';

interface SelectionCandidate {
  influencerId: string;
  handle?: string;
}

interface ConfirmScoutingSelectionDialogProps {
  candidates: readonly SelectionCandidate[];
  targetStage?: ScoutingSelectionStage;
  pending: boolean;
  error?: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmScoutingSelectionDialog({
  candidates,
  targetStage = 'Shortlisted',
  pending,
  error,
  onCancel,
  onConfirm,
}: ConfirmScoutingSelectionDialogProps) {
  const brandSelection = targetStage === 'Brand Shortlisted';
  const cancelButton = useRef<HTMLButtonElement>(null);
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !pending) {
          onCancel();
        }
      }}
    >
      <DialogContent
        className="ss-marketing"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          cancelButton.current?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle>{brandSelection ? 'Brand select' : 'Select'} {candidates.length} finalists?</DialogTitle>
          <DialogDescription>
            {brandSelection
              ? 'These creators will move to Brand Shortlisted and be treated as brand approved.'
              : 'These creators will move to Shortlisted. Performance campaigns will still await brand approval.'}
            {' '}Review the list before confirming. Other finalists will stay unchanged.
          </DialogDescription>
        </DialogHeader>
        <ul className="tw-max-h-60 tw-overflow-y-auto tw-space-y-1 tw-text-sm" aria-label="Finalists to select">
          {candidates.map((candidate) => (
            <li key={candidate.influencerId}>{candidate.handle ? `@${candidate.handle}` : candidate.influencerId}</li>
          ))}
        </ul>
        {error && (
          <p role="alert" className="tw-text-sm tw-text-red-600">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button ref={cancelButton} type="button" variant="outline" disabled={pending} onClick={onCancel}>
            Cancel
          </Button>
          <Button type="button" disabled={pending || candidates.length === 0} onClick={onConfirm}>
            {pending ? 'Selecting…' : `Confirm ${brandSelection ? 'brand selection' : 'selection'} (${candidates.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
